import { EventHandler, Unsubscribe, FederationEvents } from '../types/types';
import { EventBus, FederationEventBus } from './EventBus';

export class FederationCommunicationService {
  private eventBus: EventBus;
  private lastEventValues: Map<keyof FederationEvents, any> = new Map();
  private debugMode: boolean = false;

  constructor(eventBus: EventBus) {
    this.eventBus = eventBus;
    // Extract debug mode from FederationEventBus if available
    if (eventBus instanceof FederationEventBus) {
      this.debugMode = eventBus.isDebugMode;
    }
  }

  /**
   * Publish any event with state persistence
   */
  publish<T extends keyof FederationEvents>(eventName: T, payload: FederationEvents[T]) {
    // Store the last value for late subscribers
    this.lastEventValues.set(eventName, payload);
    if (this.debugMode) {
      console.log(`[FederationCommunication] Stored last value for: ${String(eventName)}`, payload);
    }
    this.eventBus.emit(eventName, payload);
  }

  /**
   * Subscribe to any event, getting last value immediately if available
   */
  subscribe<T extends keyof FederationEvents>(eventName: T, handler: EventHandler<T>): Unsubscribe {
    if (this.debugMode) {
      console.log(`[FederationCommunication] Subscribing to: ${String(eventName)}`);
    }
    return this.eventBus.on(eventName, handler);
  }

  /**
   * Get the last emitted value for an event (without subscribing)
   */
  getLastValue<T extends keyof FederationEvents>(eventName: T): FederationEvents[T] | undefined {
    const value = this.lastEventValues.get(eventName);
    if (this.debugMode) {
      console.log(`[FederationCommunication] Getting last value for: ${String(eventName)}`, {
        found: value !== undefined,
        value: value,
        allStoredKeys: Array.from(this.lastEventValues.keys()),
      });
    }
    return value;
  }

  /**
   * Clear stored value for an event
   */
  clearValue<T extends keyof FederationEvents>(eventName: T) {
    const hadValue = this.lastEventValues.has(eventName);
    this.lastEventValues.delete(eventName);
    if (this.debugMode) {
      console.log(`[FederationCommunication] Cleared value for: ${String(eventName)}`, { hadValue });
    }
  }

  /**
   * Debug helper: log all stored values
   */
  debugStoredValues() {
    console.log('[FederationCommunication] All stored values:', {
      count: this.lastEventValues.size,
      keys: Array.from(this.lastEventValues.keys()),
      values: Object.fromEntries(this.lastEventValues),
    });
  }
}
